import React, {
  useCallback, useEffect, useMemo, useRef, useState, ReactElement, Fragment, RefObject,
} from 'react';
import { useDispatch } from 'react-redux';
import List from '../../../components/list/list.component';
import ListItem from '../../../components/list-item/list-item.component';
import ListItemText from '../../../components/list-item-text/list-item-text.component';
import CheckAccess from '../../../components/check-access/check-access.component';
import ExpansionGroup from '../../../components/expansion-group/expansion-group.component';
import ContextMenu from '../../../components/context-menu/context-menu.component';
import DraggableItem from '../../../components/draggable-item/draggable-item.component';
import DragHandle from '../../../components/drag-handle/drag-handle.component';
import Tooltip from '../../../components/tooltip/tooltip.component';
import NoContentBlock from '../../../components/no-content-block/no-content-block.component';
import Card from '../../../components/card/card.component';
import Table from '../../../components/table/table.component';
import TableHead from '../../../components/table-head/table-head.component';
import TableBody from '../../../components/table-body/table-body.component';
import TableCell from '../../../components/table-cell/table-cell.component';
import FlexContainer from '../../../components/flex-container/flex-container.component';
import ActionButton from '../../../components/action-button/action-button.component';
import FormFieldText from '../../../components/form-field-text/form-field-text.component';
import ActionButtonContainer from '../../../components/action-button-container/action-button-container.component';
import ActionButtonNewItem from '../../../components/action-button-new-item/action-button-new-item.component';
import TableRowGroupName from '../../../components/table-row-group-name/table-row-group-name.component';
import TableRowNoContent from '../../../components/table-row-no-content/table-row-no-content.component';
import TableRowAddNewItem from '../../../components/table-row-add-new-item/table-row-add-new-item.component';
import ContentListContainerOffset
  from '../../app/common/content-list-container-offset/content-list-container-offset.component';
import DealTermRow from './deal-term-row/deal-term-row.component';
import DealTermRowForm from './deal-term-row-form/deal-term-row-form.component';
import ExpandIcon from '../../../icons/expand-icon';
import CollapseIcon from '../../../icons/collapse-icon';
import DeleteItemIcon from '../../../icons/delete-item-icon';
import MoreIcon from '../../../icons/more-icon';
import RemoveIcon from '../../../icons/remove-icon';
import StructureService from '../../../services/structure-service';
import SortService from '../../../services/sort-service';
import FormService from '../../../services/form-service';
import useArrowNavigation from '../../../hooks/use-arrow-navigation';
import useScreenSmall from '../../../hooks/use-screen-small';
import useMobileDevice from '../../../hooks/use-mobile-device';
import useCurrentUserPermissions from '../../../hooks/use-current-user-permissions';
import { saveDealTermGroupOrderSettings } from './deal-terms.action';
import { DATA_DEAL_TERM_GROUP_ID, DATA_DEAL_TERM_ID } from '../../../constants/system/data-attributes';
import {
  DEAL_TERM_CREATE, DEAL_TERM_DELETE, DEAL_TERM_GROUP_DELETE, DEAL_TERM_EDIT,
  DEAL_TERM_GROUP_EDIT, DEAL_TERM_LIST_VIEW,
} from '../../../constants/entity/permissions';
import { TIMEOUT_DEFAULT } from '../../../constants/system/timeouts';
import { SORT_TYPE_ASC } from '../../../constants/system/sort-types';

const DealTerms: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const COLUMNS_TOTAL: number = 3;
  const COLUMNS_NAME_TOTAL: number = 2;

  const {
    isEditMode,
    isNewDeal,
    isCustomizeMode,
    dealId,
    dealTermsData,
    dealTermNewItems,
    dealTermNewGroupItems,
    expandedGroups,
    onAddNewItem,
    onDeleteClick,
    onDeleteGroupClick,
    onExpansionGroupChange,
    onOpenDeleteConfirmModal,
    onOpenDeleteGroupConfirmModal,
  } = props;

  const GROUP_SORT_STATE: SortParams = useMemo((): SortParams => ({
    field: 'order',
    type: SORT_TYPE_ASC,
    isNumber: true,
  }), []);

  const dispatch: ReduxDispatch = useDispatch();
  const isSmallScreen: boolean = useScreenSmall();
  const isMobileDevice: boolean = useMobileDevice();
  const permissionList: string[] = useCurrentUserPermissions();
  const tableRefs: RefObject<Record<string, HTMLTableElement>> = useRef({});
  const inputRefs: RefObject<Record<string, HTMLElement>> = useRef({});
  const dealTermGroupFieldRefs: RefObject<Record<string, HTMLDivElement>> = useRef({});

  const [menuAnchor, setMenuAnchor]: [HTMLElement | null, Function] = useState(null);
  const [groupMenuAnchor, setGroupMenuAnchor]: [HTMLElement | null, Function] = useState(null);
  const [dealTermGroups, setDealTermGroups]: [DealTermGroup[], Function] = useState([]);
  const [activeDealTermGroupIndex, setActiveDealTermGroupIndex]: [number | null, Function] = useState(null);
  const [hoverDealTermGroupIndex, setHoverDealTermGroupIndex]: [number | null, Function] = useState(null);

  const dealTermEditPermissions: string[] = useMemo((): string[] => [DEAL_TERM_EDIT], []);
  const dealTermGroupDeletePermissions: string[] = useMemo((): string[] => [DEAL_TERM_GROUP_DELETE], []);
  const dealTermGroupEditPermissions: string[] = useMemo((): string[] => [DEAL_TERM_GROUP_EDIT], []);
  const dealTermCreatePermissions: string[] = useMemo((): string[] => [DEAL_TERM_CREATE], []);
  const dealTermListViewPermissions: string[] = useMemo((): string[] => [DEAL_TERM_LIST_VIEW], []);

  const dealTermGroupsTotal: number = dealTermGroups && dealTermGroups.length;
  const formPrefix: string = 'dealData.dealTermsData';
  const withButtons: boolean = isEditMode;
  const isDealTermGroupEditAllowed: boolean = StructureService.checkPermission(DEAL_TERM_GROUP_EDIT, permissionList);
  const isGroupOrderAllowed: boolean = isCustomizeMode && expandedGroups && !expandedGroups.length
    && !isMobileDevice && isDealTermGroupEditAllowed;
  const dealTermGroupIds: number[] = dealTermsData && dealTermsData.length
    ? dealTermsData.map((dealTermGroupItem: DealTermGroup): number => dealTermGroupItem.id) : [];

  let commonRowIndex: number = 0;

  const openActionMenu: MouseEventHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
  ): void => {
    setMenuAnchor(event.currentTarget);

    event.stopPropagation();
  }, []);

  const openGroupActionMenu: MouseEventHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
  ): void => {
    setGroupMenuAnchor(event.currentTarget);

    event.stopPropagation();
  }, []);

  const addNewItem: Function = useCallback((dealTermGroupId: number): void => {
    if (onAddNewItem) {
      onAddNewItem(dealTermGroupId, {
        id: StructureService.getNewItemId(dealTermNewItems[dealTermGroupId] || []),
        name: '',
        groupId: dealTermGroupId,
        content: '',
      });
    }
  }, [dealTermsData, dealTermNewItems]);

  const deleteClick: Function = useCallback((dealTermId: number, dealTermGroupId: number): void => {
    if (onDeleteClick) {
      onDeleteClick(dealTermId, dealTermGroupId);
    }

    if (dealTermGroups) {
      const newDealTermsGroups: DealTermGroup[] = dealTermGroups.map((
        dealTermGroupItem: DealTermGroup,
      ): DealTermGroup => ({
        ...dealTermGroupItem,
        terms: dealTermGroupItem.id === dealTermGroupId
          ? dealTermGroupItem.terms.filter((dealTermItem: DealTerm): boolean => dealTermItem.id !== dealTermId)
          : dealTermGroupItem.terms,
      }));
      setDealTermGroups(newDealTermsGroups);
    }
  }, [dealTermGroups, dealTermNewItems]);

  const deleteGroupClick: Function = useCallback((
    event: React.MouseEvent<Element, MouseEvent>, dealTermGroupId: number,
  ): void => {
    if (onDeleteGroupClick) {
      onDeleteGroupClick(dealTermGroupId);
    }

    event.stopPropagation();
  }, [dealTermNewGroupItems]);

  const closeActionMenu: MouseEventHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
  ): void => {
    setMenuAnchor(null);

    event.stopPropagation();
  }, []);

  const closeGroupActionMenu: MouseEventHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
  ): void => {
    setGroupMenuAnchor(null);

    event.stopPropagation();
  }, []);

  const onGroupFieldClick: MouseEventHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
  ): void => {
    event.stopPropagation();
  }, []);

  const actionMenuItems: ActionMenuItem[] = useMemo((): ActionMenuItem[] => [
    {
      id: 1,
      icon: <DeleteItemIcon />,
      text: 'Delete',
      permission: DEAL_TERM_DELETE,
      onClick: (event: React.MouseEvent<Element, MouseEvent>, dealTermId: number): void => {
        if (dealTermId) {
          onOpenDeleteConfirmModal(event, dealTermId);
        }
      },
    },
  ], []);

  const actionGroupMenuItems: ActionMenuItem[] = useMemo((): ActionMenuItem[] => [
    {
      id: 1,
      icon: <DeleteItemIcon />,
      text: 'Delete',
      permission: DEAL_TERM_GROUP_DELETE,
      onClick: (event: React.MouseEvent<Element, MouseEvent>, dealTermGroupId: number): void => {
        if (dealTermGroupId) {
          onOpenDeleteGroupConfirmModal(dealTermGroupId);
        }
      },
    },
  ], []);

  const onDropGroupItem: Function = useCallback((dragIndex: number, hoverIndex: number): void => {
    const newDealTermGroupIds: number[] = [...dealTermGroupIds];
    const activeDealTermGroupId: number = newDealTermGroupIds[dragIndex];
    StructureService.deleteListItemByIndex(newDealTermGroupIds, dragIndex);
    StructureService.insertListItemByIndex(newDealTermGroupIds, hoverIndex, activeDealTermGroupId);

    dispatch(saveDealTermGroupOrderSettings({
      dealId,
      dealTermGroupIds: newDealTermGroupIds.filter((dealTermGroupId: number): boolean => !!dealTermGroupId),
    }));
  }, [dealTermGroupIds, dealId]);

  const onHoverGroupItem: Function = useCallback((dragIndex: number, hoverIndex: number): void => {
    setActiveDealTermGroupIndex(dragIndex);
    setHoverDealTermGroupIndex(hoverIndex);
  }, []);

  const onReleaseGroupItem: Function = useCallback((): void => {
    setActiveDealTermGroupIndex(null);
    setHoverDealTermGroupIndex(null);
  }, []);

  useEffect((): ReactUseEffectCleanUp => {
    const timeoutId: TimeoutId | null = setTimeout((): void => {
      const dealTermGroupFields: HTMLDivElement[] = dealTermGroupFieldRefs && dealTermGroupFieldRefs.current
        ? Object.values(dealTermGroupFieldRefs.current) : [];

      if (dealTermGroupFields.length && dealTermGroupFields.length) {
        const lastDealTermGroupField: HTMLElement = dealTermGroupFields[dealTermGroupFields.length - 1];

        if (lastDealTermGroupField) {
          lastDealTermGroupField.focus();
        }
      }
    }, TIMEOUT_DEFAULT);

    return (): void => {
      clearTimeout(timeoutId);
    };
  }, [dealTermGroupsTotal]);

  useEffect((): void => {
    const initialDealTermGroups: DealTermGroup[] = dealTermsData
      ? [...dealTermsData, ...dealTermNewGroupItems] : dealTermNewGroupItems;

    setDealTermGroups([...initialDealTermGroups]);
  }, [dealTermsData, dealTermNewGroupItems, isEditMode]);

  useArrowNavigation(inputRefs);

  return (
    <CheckAccess permissions={dealTermEditPermissions} isEnabled={isEditMode || isNewDeal}>
      {
        dealTermGroups && !!dealTermGroups.length ? (
          <Card>
            <ContentListContainerOffset withTabs withButtons={withButtons}>
              {
                SortService.sort(dealTermGroups, GROUP_SORT_STATE).map((
                  dealTermGroup: DealTermGroup, dealTermGroupIndex: number,
                ): ReactElement => {
                  const formKeyDealTermGroup: string = FormService.getIndexedFormFieldKey(dealTermGroup.id);
                  const isGroupCollapsed: boolean = expandedGroups && !expandedGroups.includes(dealTermGroupIndex + 1);
                  const isNewItem: boolean = dealTermNewGroupItems && !!dealTermNewGroupItems.length
                    && !!dealTermNewGroupItems.find((dealTermNewGroup: DealTermGroup): boolean => (
                      dealTermNewGroup.id === dealTermGroup.id
                    ));

                  return (
                    <DraggableItem
                      withHighlight
                      isDisabled={!isGroupOrderAllowed}
                      isHovered={hoverDealTermGroupIndex === dealTermGroupIndex}
                      isHoverInverted={hoverDealTermGroupIndex && activeDealTermGroupIndex
                        && hoverDealTermGroupIndex > activeDealTermGroupIndex}
                      key={dealTermGroup.id}
                      itemId={dealTermGroup.id}
                      index={dealTermGroupIndex}
                      onDropItem={onDropGroupItem}
                      onHoverItem={onHoverGroupItem}
                      onReleaseItem={onReleaseGroupItem}
                    >
                      <ExpansionGroup
                        isTableGroup
                        isExpanded={(isSmallScreen && !isGroupCollapsed) || !isSmallScreen}
                        withNoHeader={!isSmallScreen}
                        key={dealTermGroup.id}
                        onChange={(event: React.SyntheticEvent): void => {
                          onExpansionGroupChange(event, dealTermGroupIndex + 1);
                        }}
                        headerComponent={isSmallScreen && (
                          !isEditMode && !isNewDeal ? (
                            <FlexContainer isSpread>
                              <FlexContainer isSpread isStart>
                                {dealTermGroup.name}
                              </FlexContainer>
                              {
                                !isSmallScreen && (
                                  <ActionButtonContainer>
                                    <CheckAccess permissions={dealTermGroupDeletePermissions}>
                                      <ActionButton
                                        isLight
                                        isActive
                                        withPopup
                                        withSmallPadding
                                        onClick={openGroupActionMenu}
                                        {...{
                                          [DATA_DEAL_TERM_GROUP_ID]: dealTermGroup.id,
                                        }}
                                      >
                                        <MoreIcon />
                                      </ActionButton>
                                    </CheckAccess>
                                  </ActionButtonContainer>
                                )
                              }
                            </FlexContainer>
                          ) : (
                            <FlexContainer isSpread>
                              <CheckAccess permissions={dealTermGroupEditPermissions}>
                                <FormFieldText isHidden name={`${formPrefix}[${formKeyDealTermGroup}].id`} />
                                <FormFieldText
                                  isLight
                                  withUnderline
                                  inputRef={(ref: HTMLDivElement): void => {
                                    if (dealTermGroupFieldRefs.current) {
                                      dealTermGroupFieldRefs.current[dealTermGroup.id] = ref;
                                    }
                                  }}
                                  name={`${formPrefix}[${formKeyDealTermGroup}].name`}
                                  placeholder="Group Name"
                                  onClick={onGroupFieldClick}
                                />
                              </CheckAccess>
                              {
                                isNewItem && (
                                  <ActionButtonContainer>
                                    <Tooltip title="Delete">
                                      <ActionButton
                                        isLight
                                        withSmallPadding
                                        onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                                          deleteGroupClick(event, dealTermGroup.id);
                                        }}
                                      >
                                        <RemoveIcon />
                                      </ActionButton>
                                    </Tooltip>
                                  </ActionButtonContainer>
                                )
                              }
                            </FlexContainer>
                          )
                        )}
                      >
                        <CheckAccess permissions={dealTermListViewPermissions}>
                          {
                            !isSmallScreen ? (
                              <Table
                                withStickyHeader
                                isFullWidth={isEditMode || isNewDeal}
                                ref={(ref: HTMLTableElement): void => {
                                  if (tableRefs.current) {
                                    tableRefs.current[dealTermGroup.id] = ref;
                                  }
                                }}
                              >
                                {
                                  !isSmallScreen && (
                                    <TableHead isHeader>
                                      {
                                        !isEditMode && !isNewDeal ? (
                                          <TableRowGroupName
                                            isButton
                                            onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                                              onExpansionGroupChange(event, dealTermGroupIndex + 1);
                                            }}
                                          >
                                            <TableCell width="30%" colSpan={COLUMNS_NAME_TOTAL}>
                                              {
                                                isGroupOrderAllowed && (
                                                  <DragHandle />
                                                )
                                              }
                                              {dealTermGroup.name}
                                            </TableCell>
                                            <TableCell isTextRight width="5%">
                                              <ActionButtonContainer>
                                                <CheckAccess permissions={dealTermGroupDeletePermissions}>
                                                  <ActionButton
                                                    isLight
                                                    isActive
                                                    withPopup
                                                    withNoPadding
                                                    onClick={openGroupActionMenu}
                                                    {...{
                                                      [DATA_DEAL_TERM_GROUP_ID]: dealTermGroup.id,
                                                    }}
                                                  >
                                                    <MoreIcon />
                                                  </ActionButton>
                                                </CheckAccess>
                                                <ActionButton
                                                  isLight
                                                  withNoPadding
                                                  onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                                                    onExpansionGroupChange(event, dealTermGroupIndex + 1);
                                                  }}
                                                >
                                                  {!isGroupCollapsed ? <CollapseIcon /> : <ExpandIcon />}
                                                </ActionButton>
                                              </ActionButtonContainer>
                                            </TableCell>
                                          </TableRowGroupName>
                                        ) : (
                                          <TableRowGroupName
                                            isButton
                                            onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                                              onExpansionGroupChange(event, dealTermGroupIndex + 1);
                                            }}
                                          >
                                            <CheckAccess permissions={dealTermGroupEditPermissions}>
                                              <TableCell width="30%">
                                                <FlexContainer isSpread>
                                                  <FormFieldText
                                                    isHidden
                                                    name={`${formPrefix}[${formKeyDealTermGroup}].id`}
                                                  />
                                                  <FormFieldText
                                                    isLight
                                                    withUnderline
                                                    inputRef={(ref: HTMLDivElement): void => {
                                                      if (dealTermGroupFieldRefs.current) {
                                                        dealTermGroupFieldRefs.current[dealTermGroup.id] = ref;
                                                      }
                                                    }}
                                                    name={`${formPrefix}[${formKeyDealTermGroup}].name`}
                                                    placeholder="Group Name"
                                                    onClick={onGroupFieldClick}
                                                  />
                                                </FlexContainer>
                                              </TableCell>
                                              <TableCell />
                                              <TableCell isTextRight width="5%">
                                                <ActionButtonContainer>
                                                  {
                                                    isNewItem && (
                                                      <Tooltip title="Delete">
                                                        <ActionButton
                                                          isLight
                                                          withNoPadding
                                                          onClick={(
                                                            event: React.MouseEvent<Element, MouseEvent>,
                                                          ): void => (
                                                            deleteGroupClick(event, dealTermGroup.id)
                                                          )}
                                                        >
                                                          <RemoveIcon />
                                                        </ActionButton>
                                                      </Tooltip>
                                                    )
                                                  }
                                                  {
                                                    !isSmallScreen && (
                                                      <ActionButton
                                                        isLight
                                                        withNoPadding
                                                        onClick={(
                                                          event: React.MouseEvent<Element, MouseEvent>,
                                                        ): void => (
                                                          onExpansionGroupChange(event, dealTermGroupIndex + 1)
                                                        )}
                                                      >
                                                        {!isGroupCollapsed ? <CollapseIcon /> : <ExpandIcon />}
                                                      </ActionButton>
                                                    )
                                                  }
                                                </ActionButtonContainer>
                                              </TableCell>
                                            </CheckAccess>
                                          </TableRowGroupName>
                                        )
                                      }
                                    </TableHead>
                                  )
                                }
                                <TableBody
                                  isPlain
                                  withZebraRows
                                  withBorderedCells
                                  withFixedCellsSize
                                  isHidden={!isSmallScreen && isGroupCollapsed}
                                  withFixedCellsSizeExtended={isEditMode}
                                >
                                  {
                                    dealTermGroup.terms && !!dealTermGroup.terms.length
                                    && dealTermGroup.terms.map((dealTerm: DealTerm): ReactElement => {
                                      const commonDealTermIndex: number = ++commonRowIndex;

                                      return (
                                        <DealTermRow
                                          isNewDeal={isNewDeal}
                                          isEditMode={isEditMode}
                                          key={dealTerm.id}
                                          dealTermItem={dealTerm}
                                          dealTermGroup={dealTermGroup}
                                          dealTermIndex={commonDealTermIndex}
                                          inputRefs={inputRefs}
                                          actionMenuItems={actionMenuItems}
                                          onDeleteClick={deleteClick}
                                          onOpenActionMenu={openActionMenu}
                                        />
                                      );
                                    })
                                  }
                                  {
                                    (!isEditMode && !isNewDeal) && (isSmallScreen
                                      || (!isSmallScreen && !isGroupCollapsed))
                                    && (!dealTermGroup.terms || !dealTermGroup.terms.length) && (
                                      <TableRowNoContent>
                                        <TableCell colSpan={COLUMNS_TOTAL}>
                                          No terms in this group
                                        </TableCell>
                                      </TableRowNoContent>
                                    )
                                  }
                                  {
                                    (isSmallScreen || (!isSmallScreen && !isGroupCollapsed))
                                    && (isEditMode || isNewDeal) && dealTermNewItems[dealTermGroup.id]
                                    && !!dealTermNewItems[dealTermGroup.id].length
                                    && dealTermNewItems[dealTermGroup.id].map((dealTerm: DealTerm): ReactElement => {
                                      const commonDealTermIndex: number = ++commonRowIndex;

                                      return (
                                        <DealTermRowForm
                                          isActive={!isNewDeal}
                                          isListItem={isSmallScreen}
                                          key={dealTerm.id}
                                          inputRefs={inputRefs}
                                          dealTermIndex={commonDealTermIndex}
                                          dealTermGroupData={dealTermGroup}
                                          dealTermData={dealTerm}
                                          onDeleteClick={(): void => {
                                            deleteClick(dealTerm.id, dealTermGroup.id);
                                          }}
                                        />
                                      );
                                    })
                                  }
                                  {
                                    (isSmallScreen || (!isSmallScreen && !isGroupCollapsed))
                                    && (isEditMode || isNewDeal) && (
                                      <CheckAccess permissions={dealTermCreatePermissions}>
                                        <TableRowAddNewItem
                                          label="Add New Term"
                                          colSpan={COLUMNS_TOTAL}
                                          onClick={(): void => {
                                            addNewItem(dealTermGroup.id);
                                          }}
                                        />
                                      </CheckAccess>
                                    )
                                  }
                                </TableBody>
                              </Table>
                            ) : (
                              <List>
                                {
                                  dealTermGroup.terms.map((dealTerm: DealTerm): ReactElement => (
                                    <Fragment key={dealTerm.id}>
                                      {
                                        (isEditMode || isNewDeal) ? (
                                          <DealTermRowForm
                                            isListItem
                                            dealTermGroupData={dealTermGroup}
                                            dealTermData={dealTerm}
                                            onDeleteClick={(): void => {
                                              deleteClick(dealTerm.id, dealTermGroup.id);
                                            }}
                                          />
                                        ) : (
                                          <ListItem isButton isExtended>
                                            <ListItemText isBlock>
                                              {dealTerm.name}
                                            </ListItemText>
                                            <ListItemText isBlock isBlockRight isInline isMultiline>
                                              {dealTerm.content}
                                            </ListItemText>
                                          </ListItem>
                                        )
                                      }
                                    </Fragment>
                                  ))
                                }
                                {
                                  (!isEditMode && !isNewDeal) && (isSmallScreen
                                    || (!isSmallScreen && !isGroupCollapsed))
                                  && (!dealTermGroup.terms || !dealTermGroup.terms.length) && (
                                    <NoContentBlock>
                                      No terms in this group
                                    </NoContentBlock>
                                  )
                                }
                                {
                                  (isEditMode || isNewDeal) && dealTermNewItems[dealTermGroup.id]
                                  && !!dealTermNewItems[dealTermGroup.id].length
                                  && dealTermNewItems[dealTermGroup.id].map((dealTerm: DealTerm): ReactElement => {
                                    const commonDealTermIndex: number = ++commonRowIndex;

                                    return (
                                      <DealTermRowForm
                                        isListItem
                                        key={dealTerm.id}
                                        dealTermGroupData={dealTermGroup}
                                        dealTermData={dealTerm}
                                        dealTermIndex={commonDealTermIndex}
                                        onDeleteClick={(): void => {
                                          deleteClick(dealTerm.id, dealTermGroup.id);
                                        }}
                                      />
                                    );
                                  })
                                }
                                {
                                  (isEditMode || isNewDeal) && (
                                    <CheckAccess permissions={dealTermCreatePermissions}>
                                      <Card
                                        isContainer
                                        isSelectable
                                        withSmallPadding
                                        onClick={(): void => {
                                          addNewItem(dealTermGroup.id);
                                        }}
                                      >
                                        <ActionButtonNewItem withNoHighlight label="Add New Term" />
                                      </Card>
                                    </CheckAccess>
                                  )
                                }
                              </List>
                            )
                          }
                        </CheckAccess>
                      </ExpansionGroup>
                    </DraggableItem>
                  );
                })
              }
            </ContentListContainerOffset>
          </Card>
        ) : (
          <NoContentBlock>
            No terms
          </NoContentBlock>
        )
      }
      <ContextMenu
        menuAnchor={groupMenuAnchor}
        actionMenuItems={actionGroupMenuItems}
        dataAttributeEntityId={DATA_DEAL_TERM_GROUP_ID}
        onClose={closeGroupActionMenu}
      />
      <ContextMenu
        menuAnchor={menuAnchor}
        actionMenuItems={actionMenuItems}
        dataAttributeEntityId={DATA_DEAL_TERM_ID}
        onClose={closeActionMenu}
      />
    </CheckAccess>
  );
});

export default DealTerms;
